// ---------------------------------------------------------------------------
// Rally score derivation.
//
// The rally list is the single source of truth: each rally records only who
// won it. The running point/game/set score is derived by replaying that
// sequence forward from the opening server, so flipping an earlier winner or
// deleting a rally rescores everything after it for free.
//
// This is the mirror image of utils/scoringTree.js, which works *backwards*
// from score strings already stored on stroke annotations. The token
// conventions ('AD' rather than 'A') are kept in sync with that module.
// ---------------------------------------------------------------------------

const POINT_TOKENS = ['0', '15', '30', '40'];

const TIEBREAK_AT = 6;      // games apiece that triggers a tiebreak
const TIEBREAK_TARGET = 7;  // points needed to win one, by a margin of 2
const GAMES_TO_WIN_SET = 6;

export const other = (side) => (side === 'near' ? 'far' : 'near');

/**
 * Format a game's point score from raw point counts, always server first.
 * Outside a tiebreak this is tennis notation; inside one it is plain integers.
 */
export function formatPointScore(serverPoints, receiverPoints, { isTiebreak, adScoring }) {
  if (isTiebreak) return `${serverPoints}-${receiverPoints}`;

  // Below deuce both sides map straight onto 0/15/30/40.
  if (serverPoints < 3 || receiverPoints < 3) {
    return `${POINT_TOKENS[serverPoints]}-${POINT_TOKENS[receiverPoints]}`;
  }
  // At 3-3 or beyond, no-ad has no states left to describe: the next point
  // decides the game, so the only reachable score is deuce.
  if (!adScoring || serverPoints === receiverPoints) return '40-40';
  return serverPoints > receiverPoints ? 'AD-40' : '40-AD';
}

/** Whether `winnerPoints` vs `loserPoints` closes out the game. */
function gameIsWon(winnerPoints, loserPoints, { isTiebreak, adScoring }) {
  if (isTiebreak) {
    return winnerPoints >= TIEBREAK_TARGET && winnerPoints - loserPoints >= 2;
  }
  if (!adScoring) return winnerPoints >= 4;
  return winnerPoints >= 4 && winnerPoints - loserPoints >= 2;
}

/** Whether `winnerGames` vs `loserGames` closes out the set. */
function setIsWon(winnerGames, loserGames) {
  if (winnerGames >= GAMES_TO_WIN_SET && winnerGames - loserGames >= 2) return true;
  // 7-6 is the only one-game margin that ends a set, and it can only arrive
  // via the tiebreak played at 6-6.
  return winnerGames === TIEBREAK_AT + 1 && loserGames === TIEBREAK_AT;
}

/**
 * Replay the rally sequence and report the state ENTERING each rally.
 *
 * @param {Array}  rallies       - [{ id, startTime, winner: 'near'|'far'|null }]
 * @param {string} initialServer - 'near' | 'far'; who served the very first point
 * @param {boolean} adScoring    - true for advantage scoring, false for a deciding point at 40-40
 * @returns {Array} one entry per rally, in start-time order:
 *   { id, server, nominalServer, pointScore, gameScore, setScore, isTiebreak, gameNumber }
 *   gameNumber counts games from 0 across the whole match.
 *   Score fields are null once an unscored rally makes the rest unknowable.
 */
export function computeScoreboard(rallies, initialServer, adScoring = false) {
  const ordered = [...rallies].sort((a, b) => a.startTime - b.startTime);

  if (initialServer !== 'near' && initialServer !== 'far') {
    return ordered.map((rally) => unknownEntry(rally));
  }

  // Absolute per-side tallies; the server-first view is applied at format time.
  let sets = { near: 0, far: 0 };
  let games = { near: 0, far: 0 };
  let points = { near: 0, far: 0 };

  // The player who serves the current game. During a tiebreak the actual
  // server alternates every two points, so this stays pinned to whoever served
  // the tiebreak's first point and keeps the score strings in one perspective.
  let nominalServer = initialServer;
  let isTiebreak = false;
  let gameNumber = 0;
  // Once a rally has no winner the score can no longer be advanced, so every
  // later rally is reported as unknown rather than silently wrong.
  let stalled = false;

  return ordered.map((rally) => {
    if (stalled) return unknownEntry(rally);

    const receiver = other(nominalServer);
    // Tiebreak serving: one point, then alternate every two.
    const server = isTiebreak && Math.floor((points.near + points.far + 1) / 2) % 2 === 1
      ? receiver
      : nominalServer;

    const entry = {
      id: rally.id,
      server,
      nominalServer,
      isTiebreak,
      gameNumber,
      pointScore: formatPointScore(points[nominalServer], points[receiver], { isTiebreak, adScoring }),
      gameScore: `${games[nominalServer]}-${games[receiver]}`,
      setScore: `${sets[nominalServer]}-${sets[receiver]}`,
    };

    if (rally.winner !== 'near' && rally.winner !== 'far') {
      stalled = true;
      return entry;
    }

    const winner = rally.winner;
    const loser = other(winner);
    points[winner] += 1;

    if (!gameIsWon(points[winner], points[loser], { isTiebreak, adScoring })) return entry;

    games[winner] += 1;
    gameNumber += 1;
    points = { near: 0, far: 0 };

    // The tiebreak's first server receives first in the next set, which is the
    // same as simply continuing to alternate from the nominal server.
    const nextServer = other(nominalServer);

    if (setIsWon(games[winner], games[loser])) {
      sets[winner] += 1;
      games = { near: 0, far: 0 };
      isTiebreak = false;
    } else {
      isTiebreak = games.near === TIEBREAK_AT && games.far === TIEBREAK_AT;
    }

    nominalServer = nextServer;
    return entry;
  });
}

function unknownEntry(rally) {
  return {
    id: rally.id,
    server: null,
    nominalServer: null,
    isTiebreak: false,
    gameNumber: null,
    pointScore: null,
    gameScore: null,
    setScore: null,
  };
}
